import React from 'react';

import { AdminPlatformUser } from 'src/api/adminUsers';
import {
  KycRecord,
  formatDecisionCode,
  formatIdentificationType,
  formatKycName,
  formatNfcValidated,
  formatRiskScore,
  formatVeriffTerm,
  getKycUserId,
  isVeriffRecord,
} from 'src/api/adminKyc';
import { convertDate } from 'src/components/Table/helpers';

import LegacyDocuments from './LegacyDocuments';
import VeriffMedia from './VeriffMedia';
import {
  Card,
  CardTitle,
  ChipRow,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  MediaNotice,
  RiskChip,
  StatusBadge,
  UserLink,
} from './styled';

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const { date, time } = convertDate(String(value));
  return `${date} - ${time}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  return convertDate(String(value)).date;
};

const formatBool = (value?: boolean) => {
  if (value == null) return '—';
  return value ? 'Yes' : 'No';
};

type KycRecordBodyProps = {
  record: KycRecord;
  /** Fills in email and BxLend ID, which the KYC record itself does not carry. */
  owner?: AdminPlatformUser | null;
  onOpenUser?: () => void;
};

/**
 * The read-only half of a KYC record. Veriff and legacy MANUAL records render
 * mutually exclusive blocks: Veriff keeps the document and selfie media, so
 * those records have no images to show.
 */
const KycRecordBody: React.FC<KycRecordBodyProps> = ({ record, owner, onOpenUser }) => {
  const isVeriff = isVeriffRecord(record);
  const veriff = record.veriff;
  const ownerId = getKycUserId(record);
  const displayName = formatKycName(record.name) || '—';

  return (
    <>
      <Card>
        <CardTitle>Customer</CardTitle>
        <DetailGrid>
          <DetailItem>
            <DetailLabel>Name</DetailLabel>
            <DetailValue $muted={displayName === '—'}>{displayName}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>User</DetailLabel>
            {ownerId && onOpenUser ? (
              <UserLink type="button" onClick={onOpenUser}>
                {owner?.email || ownerId}
              </UserLink>
            ) : (
              <DetailValue $muted={!ownerId}>{owner?.email || ownerId || '—'}</DetailValue>
            )}
          </DetailItem>
          <DetailItem>
            <DetailLabel>BxLend ID</DetailLabel>
            <DetailValue $muted={!owner?.bxlend_id}>{owner?.bxlend_id || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Country</DetailLabel>
            <DetailValue $muted={!record.country_code}>{record.country_code || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>KYC ID</DetailLabel>
            <DetailValue>{record.id}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Resubmission allowed</DetailLabel>
            <DetailValue>{formatBool(record.resubmission_allowed)}</DetailValue>
          </DetailItem>
          <DetailItem $span>
            <DetailLabel>Last message to customer</DetailLabel>
            <DetailValue $muted={!record.response_message}>
              {record.response_message || '—'}
            </DetailValue>
          </DetailItem>
        </DetailGrid>
      </Card>

      {isVeriff ? (
        <>
          <Card>
            <CardTitle>Verification</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Session state</DetailLabel>
                <DetailValue>
                  {veriff?.status ? (
                    <StatusBadge $status={String(veriff.status)}>
                      {formatVeriffTerm(veriff.status)}
                    </StatusBadge>
                  ) : (
                    '—'
                  )}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Decision</DetailLabel>
                <DetailValue $muted={veriff?.decision_code == null}>
                  {formatDecisionCode(veriff?.decision_code)}
                </DetailValue>
              </DetailItem>
              <DetailItem $span>
                <DetailLabel>Reason</DetailLabel>
                <DetailValue $muted={!veriff?.reason}>
                  {veriff?.reason || '—'}
                  {veriff?.reason_code != null ? ` (code ${veriff.reason_code})` : ''}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Risk score</DetailLabel>
                <DetailValue $muted={veriff?.risk_score == null}>
                  {formatRiskScore(veriff?.risk_score)}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Decided</DetailLabel>
                <DetailValue $muted={!veriff?.decision_time}>
                  {formatDateTime(veriff?.decision_time)}
                </DetailValue>
              </DetailItem>
              <DetailItem $span>
                <DetailLabel>Risk labels</DetailLabel>
                {veriff?.risk_labels?.length ? (
                  <ChipRow>
                    {veriff.risk_labels.map((label) => (
                      <RiskChip key={label}>{formatVeriffTerm(label)}</RiskChip>
                    ))}
                  </ChipRow>
                ) : (
                  <DetailValue $muted>—</DetailValue>
                )}
              </DetailItem>
              <DetailItem>
                <DetailLabel>Session opened</DetailLabel>
                <DetailValue $muted={!veriff?.session_created_at}>
                  {formatDateTime(veriff?.session_created_at)}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Session ID</DetailLabel>
                <DetailValue $muted={!veriff?.session_id}>{veriff?.session_id || '—'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Attempt ID</DetailLabel>
                <DetailValue $muted={!veriff?.attempt_id}>{veriff?.attempt_id || '—'}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>

          <Card>
            <CardTitle>Person on the document</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>First name</DetailLabel>
                <DetailValue $muted={!veriff?.person?.first_name}>
                  {veriff?.person?.first_name || '—'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Last name</DetailLabel>
                <DetailValue $muted={!veriff?.person?.last_name}>
                  {veriff?.person?.last_name || '—'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Date of birth</DetailLabel>
                <DetailValue $muted={!veriff?.person?.date_of_birth}>
                  {veriff?.person?.date_of_birth || '—'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Nationality</DetailLabel>
                <DetailValue $muted={!veriff?.person?.nationality}>
                  {veriff?.person?.nationality || '—'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Gender</DetailLabel>
                <DetailValue $muted={!veriff?.person?.gender}>
                  {veriff?.person?.gender || '—'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>ID number</DetailLabel>
                <DetailValue $muted={!veriff?.person?.id_number}>
                  {veriff?.person?.id_number || '—'}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>

          <Card>
            <CardTitle>Document</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Type</DetailLabel>
                <DetailValue $muted={!(veriff?.document?.type || record.identification_type)}>
                  {formatIdentificationType(veriff?.document?.type || record.identification_type)}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Number</DetailLabel>
                <DetailValue $muted={!veriff?.document?.number}>
                  {veriff?.document?.number || '—'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Issuing country</DetailLabel>
                <DetailValue $muted={!veriff?.document?.country}>
                  {veriff?.document?.country || '—'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>NFC validated</DetailLabel>
                <DetailValue $muted={veriff?.document?.nfc_validated == null}>
                  {formatNfcValidated(veriff?.document?.nfc_validated)}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Valid from</DetailLabel>
                <DetailValue $muted={!veriff?.document?.valid_from}>
                  {formatDate(veriff?.document?.valid_from)}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Valid until</DetailLabel>
                <DetailValue $muted={!veriff?.document?.valid_until}>
                  {formatDate(veriff?.document?.valid_until)}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>

          <Card>
            <CardTitle>Document and selfie</CardTitle>
            {veriff?.session_id ? (
              <VeriffMedia kycId={record.id} />
            ) : (
              <MediaNotice>No Veriff session on this record yet, so there is no media to show.</MediaNotice>
            )}
          </Card>
        </>
      ) : (
        <>
          <Card>
            <CardTitle>Application</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Identity type</DetailLabel>
                <DetailValue $muted={!record.identification_type}>
                  {formatIdentificationType(record.identification_type)}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>City</DetailLabel>
                <DetailValue $muted={!record.address?.city}>{record.address?.city || '—'}</DetailValue>
              </DetailItem>
              <DetailItem $span>
                <DetailLabel>Address</DetailLabel>
                <DetailValue $muted={!record.address?.full_address}>
                  {[record.address?.full_address, record.address?.pin_code]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Terms accepted</DetailLabel>
                <DetailValue>{formatBool(record.terms_and_conditions_consent)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Privacy policy accepted</DetailLabel>
                <DetailValue>{formatBool(record.privacy_policy_consent)}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>

          <Card>
            <CardTitle>Documents</CardTitle>
            <LegacyDocuments record={record} />
          </Card>
        </>
      )}
    </>
  );
};

export default KycRecordBody;
